import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * AuthContext supports BOTH sign-in paths:
 *  - `login()` = Emergent-managed Google OAuth (redirect flow)
 *  - `loginWithPassword()` / `signupWithPassword()` = new email/password path
 *
 * All three converge on the same session cookie + `/auth/me` bootstrap so the
 * rest of the app doesn't care which method was used.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/grove";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser({ ...data, needs_username: !data.username, needs_verification: data.auth_method === "password" && !data.email_verified });
    return data;
  };

  const signupWithPassword = async ({ email, password, username }) => {
    const { data } = await api.post("/auth/signup", { email, password, username });
    setUser({ ...data, needs_username: !data.username, needs_verification: data.auth_method === "password" && !data.email_verified });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        loginWithPassword,
        signupWithPassword,
        logout,
        refresh: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
